import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LibraryTrackMeta,
  clearAllTracks,
  clearDirHandle,
  computeTrackId,
  deleteTrack as deleteTrackFromDb,
  isAudioFile,
  iterAudioFilesFromHandle,
  parseFileName,
  readAllTracks,
  readDirHandle,
  saveDirHandle,
  writeTracks,
} from './libraryCache';

/**
 * Hybrid device-library manager. On supported browsers (Chrome/Edge,
 * Android Chrome), uses the File System Access API + a stored directory
 * handle so re-visits auto-sync with the same folder. On other browsers
 * (Safari, iOS, Firefox mostly), falls back to a `<input webkitdirectory>`
 * or multi-file picker.
 *
 * Playback needs a live `File` object (blob URL). We store metadata in
 * IndexedDB so the list renders instantly on cold visit, but Files are
 * held in a session-only ref — meaning cached rows without a live File
 * can't be played until the user regrants directory access (or re-picks).
 * We expose that state as `needsRegrant` so the page can show one CTA.
 */
export interface LibraryTrack extends LibraryTrackMeta {
  hasFile: boolean;       // false = cached metadata only, can't play yet
}

export const SUPPORTS_FS_ACCESS =
  typeof window !== 'undefined' && typeof (window as any).showDirectoryPicker === 'function';

export function useLibrary() {
  const [tracks, setTracks] = useState<LibraryTrack[]>([]);
  const [dirHandle, setDirHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [dirName, setDirName] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const [lastScanAt, setLastScanAt] = useState<number>(0);
  const [needsRegrant, setNeedsRegrant] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Live File handles keyed by track id — session only, not persisted.
  const filesRef = useRef<Map<string, File>>(new Map());

  const getFile = useCallback((id: string): File | undefined => {
    return filesRef.current.get(id);
  }, []);

  const upsertFromCache = useCallback((meta: LibraryTrackMeta[]) => {
    const list = meta.map((m) => ({
      ...m,
      hasFile: filesRef.current.has(m.id),
    }));
    // Sort newest-first by importedAt so recently added files rise.
    list.sort((a, b) => b.importedAt - a.importedAt);
    setTracks(list);
  }, []);

  // Initial hydrate — read IndexedDB cache immediately, then attempt
  // to reopen the directory handle (Chrome/Edge/Android only).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cached = await readAllTracks();
      if (cancelled) return;
      upsertFromCache(cached);
      if (SUPPORTS_FS_ACCESS) {
        const h = await readDirHandle();
        if (cancelled) return;
        if (h) {
          setDirHandle(h);
          setDirName(h.name || '');
          // Auto-scan without prompting: try `queryPermission` first
          // (silent). If already 'granted', scan straight away. Otherwise
          // wait for the user to tap "Reconnect folder" — most browsers
          // won't grant permission without a user gesture anyway.
          try {
            // @ts-expect-error: permissions API on FS handles is spec but not always typed
            const perm = await h.queryPermission({ mode: 'read' });
            if (perm === 'granted') {
              await rescanDir(h, cached);
            } else if (cached.length > 0) {
              setNeedsRegrant(true);
            }
          } catch { /* older browser without queryPermission */ }
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rescanDir = useCallback(async (
    handle: FileSystemDirectoryHandle,
    existingCache?: LibraryTrackMeta[],
  ) => {
    setIsScanning(true);
    setError(null);
    try {
      const priorById = new Map<string, LibraryTrackMeta>();
      const prior = existingCache || (await readAllTracks());
      for (const p of prior) priorById.set(p.id, p);

      const seen = new Set<string>();
      const nextMeta: LibraryTrackMeta[] = [];
      for await (const { file, relativePath } of iterAudioFilesFromHandle(handle)) {
        const id = await computeTrackId(relativePath, file.size, file.lastModified);
        seen.add(id);
        filesRef.current.set(id, file);
        const parsed = parseFileName(file.name);
        const prior = priorById.get(id);
        nextMeta.push({
          id,
          name: file.name,
          title: parsed.title,
          artist: parsed.artist,
          size: file.size,
          mimeType: file.type,
          relativePath,
          lastModified: file.lastModified,
          importedAt: prior?.importedAt || Date.now(),
          lastPlayedAt: prior?.lastPlayedAt,
        });
      }

      // Anything in the prior cache that WASN'T seen on this scan is a
      // file the user removed from disk — drop from the library. This is
      // the "sync" behaviour the user asked for.
      const removedIds = prior.filter((p) => !seen.has(p.id)).map((p) => p.id);
      await Promise.all(removedIds.map((id) => deleteTrackFromDb(id)));
      for (const id of removedIds) filesRef.current.delete(id);

      await writeTracks(nextMeta);
      upsertFromCache(nextMeta);
      setLastScanAt(Date.now());
      setNeedsRegrant(false);
    } catch (e: any) {
      setError(e?.message || 'Scan failed');
    } finally {
      setIsScanning(false);
    }
  }, [upsertFromCache]);

  /**
   * Primary "Import music" action. Chooses the best available flow:
   * File System Access (persistent) → falls through to folder-picker →
   * falls through to multi-file picker. Whichever picker completes,
   * we ingest what came back.
   */
  const pickFolder = useCallback(async () => {
    setError(null);
    if (SUPPORTS_FS_ACCESS) {
      try {
        // @ts-expect-error: showDirectoryPicker spec is Chromium-only for now
        const handle: FileSystemDirectoryHandle = await window.showDirectoryPicker({
          id: 'mixgenie-music',
          mode: 'read',
          startIn: 'music',
        });
        setDirHandle(handle);
        setDirName(handle.name || '');
        await saveDirHandle(handle);
        await rescanDir(handle);
      } catch (e: any) {
        if (e?.name !== 'AbortError') setError(e?.message || 'Folder pick failed');
      }
    } else {
      // No FS Access — the LibraryPage renders <input webkitdirectory>
      // buttons directly and calls ingestFileList() with what's picked.
      setError('Folder auto-sync isn\'t supported here — use "Pick files" below.');
    }
  }, [rescanDir]);

  const reconnectFolder = useCallback(async () => {
    if (!dirHandle) return;
    setError(null);
    try {
      // @ts-expect-error: requestPermission on FS handle
      const perm = await dirHandle.requestPermission({ mode: 'read' });
      if (perm === 'granted') {
        await rescanDir(dirHandle);
      } else {
        setError('Permission denied. Try Import folder again to pick a folder.');
      }
    } catch (e: any) {
      setError(e?.message || 'Reconnect failed');
    }
  }, [dirHandle, rescanDir]);

  /**
   * Fallback ingest for browsers without FS Access (or when the user just
   * wants to add one-off files). Called from `<input type="file">` change.
   */
  const ingestFileList = useCallback(async (list: FileList | null) => {
    if (!list || !list.length) return;
    setIsScanning(true);
    setError(null);
    try {
      const cached = await readAllTracks();
      const priorById = new Map(cached.map((p) => [p.id, p] as const));
      const nextMeta: LibraryTrackMeta[] = [];
      for (const f of Array.from(list)) {
        if (!isAudioFile(f.name, f.type)) continue;
        // webkitRelativePath is only set for folder picks; for individual
        // files it's empty and we synthesize a bare filename path.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rel = (f as any).webkitRelativePath || f.name;
        const id = await computeTrackId(rel, f.size, f.lastModified);
        filesRef.current.set(id, f);
        const parsed = parseFileName(f.name);
        const prior = priorById.get(id);
        nextMeta.push({
          id,
          name: f.name,
          title: parsed.title,
          artist: parsed.artist,
          size: f.size,
          mimeType: f.type,
          relativePath: rel,
          lastModified: f.lastModified,
          importedAt: prior?.importedAt || Date.now(),
          lastPlayedAt: prior?.lastPlayedAt,
        });
      }
      // Merge with existing cache — don't delete anything the user
      // didn't explicitly remove. This is add-only (unlike FS Access
      // rescan, which mirrors the folder).
      const mergedById = new Map<string, LibraryTrackMeta>();
      for (const p of cached) mergedById.set(p.id, p);
      for (const n of nextMeta) mergedById.set(n.id, n);
      const merged = Array.from(mergedById.values());
      await writeTracks(merged);
      upsertFromCache(merged);
      setLastScanAt(Date.now());
    } catch (e: any) {
      setError(e?.message || 'Import failed');
    } finally {
      setIsScanning(false);
    }
  }, [upsertFromCache]);

  const removeTrack = useCallback(async (id: string) => {
    filesRef.current.delete(id);
    await deleteTrackFromDb(id);
    const current = await readAllTracks();
    upsertFromCache(current);
  }, [upsertFromCache]);

  const clearAll = useCallback(async () => {
    filesRef.current.clear();
    await Promise.all([clearAllTracks(), clearDirHandle()]);
    setDirHandle(null);
    setDirName('');
    setTracks([]);
    setLastScanAt(Date.now());
  }, []);

  const tracksWithLive = useMemo(
    () => tracks.map((t) => ({ ...t, hasFile: filesRef.current.has(t.id) })),
    // filesRef is stable but we want to recompute after every scan/ingest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tracks, lastScanAt],
  );

  return {
    tracks: tracksWithLive,
    dirName,
    isScanning,
    lastScanAt,
    needsRegrant,
    error,
    supportsAutoSync: SUPPORTS_FS_ACCESS,
    getFile,
    pickFolder,
    reconnectFolder,
    ingestFileList,
    removeTrack,
    clearAll,
  };
}
